/**
 * Alice Skills / Workflows + Command Aliases — code-defined, drift-proof.
 *
 * Layering (keep this distinction):
 *   - Tools (copilot-tools.ts) are ATOMIC capabilities Alice can call.
 *   - Workflows/skills (here) are reusable MULTI-STEP behaviors that use tools.
 *     They are instruction-wrapped: the chat route injects a workflow's
 *     `instructions` into Alice's turn and she executes it with the normal tool
 *     loop. There is no separate deterministic runtime yet (by design).
 *   - Command aliases (here) are shortcuts (slash / phrase / voice intent) that
 *     trigger a workflow.
 *
 * Frontend-safe: metadata + instruction text only. No secrets, no execution.
 * Workflows must NOT perform external side effects in this version (draft only).
 */

export type AliceWorkflowStatus = 'draft' | 'testing' | 'active' | 'paused' | 'retired';

export type AliceWorkflowVisibility =
  | 'public'
  | 'admin'
  | 'internal'
  | 'private'
  | 'private_hidden';

export type AliceRiskLevel = 0 | 1 | 2 | 3 | 4;

export type AliceApprovalMode = 'none' | 'batch' | 'explicit' | 'dakota_required';

export interface AliceWorkflowIO {
  key: string;
  label: string;
  description?: string;
}

export interface AliceWorkflowDefinition {
  workflowKey: string;
  displayName: string;
  description: string;
  status: AliceWorkflowStatus;
  visibility: AliceWorkflowVisibility;
  riskLevel: AliceRiskLevel;
  approvalMode: AliceApprovalMode;
  /** Slash-command triggers (e.g. "/client-summary"). */
  triggerAliases: string[];
  /** Phrase / voice-intent triggers (e.g. "I'm in the doghouse"). */
  voiceTriggers: string[];
  requiredInputs: AliceWorkflowIO[];
  optionalInputs: AliceWorkflowIO[];
  /** Tool names (from COPILOT_TOOLS) the workflow expects to use. */
  toolsUsed: string[];
  /** Instruction block injected into Alice's turn when the workflow runs. */
  instructions: string;
  outputFormat: string;
  enabled: boolean;
}

export type AliceAliasTriggerType = 'slash' | 'voice_intent' | 'phrase';
export type AliceAliasTargetType = 'workflow';

export interface AliceCommandAlias {
  aliasKey: string;
  triggerText: string;
  triggerType: AliceAliasTriggerType;
  targetType: AliceAliasTargetType;
  targetKey: string;
  visibility: AliceWorkflowVisibility;
  requiresConfirmation: boolean;
  enabled: boolean;
}

const NO_SIDE_EFFECTS =
  'Do NOT perform external side effects: never send email, text, or messages, and never write to external systems. Any message you produce is DRAFT ONLY for Dakota to review.';

export const ALICE_WORKFLOWS: readonly AliceWorkflowDefinition[] = [
  {
    workflowKey: 'client.project_summary',
    displayName: 'Client Project Summary',
    description: 'An at-a-glance summary of a client/organization and its active work.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 0,
    approvalMode: 'none',
    triggerAliases: ['/client-summary', '/project-summary'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'client', label: 'Client / Organization', description: 'Name or record id; falls back to the on-screen org.' },
    ],
    toolsUsed: ['find_client', 'get_organization', 'search_tasks', 'search_communications'],
    instructions: [
      '1. Resolve the organization/client. If an organization id is already in the page context, use it; otherwise use find_client on the provided name.',
      '2. Load the organization with get_organization.',
      '3. Search related tasks with search_tasks (filter by the client id).',
      '4. If parent/project tasks exist, group the summary by parent task/project. If none, summarize the top 3 most recently updated tasks OR all tasks updated in the last 10 days — whichever gives better context.',
      '5. Pull recent communications with search_communications (filter by the client id) if available.',
      '6. Highlight anything involving website changes, content changes, proposals, billing, client blockers, calls, or waiting-on-client items.',
      '7. Include open-record references / links where supported (rec ids).',
    ].join('\n'),
    outputFormat:
      'Sections: Current status · Recent activity · Open blockers · Suggested next action · Important links.',
    enabled: true,
  },
  {
    workflowKey: 'proposal.readiness_check',
    displayName: 'Proposal Readiness Check',
    description: 'Review a draft proposal and flag what is missing before it is sent. Draft-only.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/proposal-check', '/proposal-readiness'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'proposalId', label: 'Proposal', description: 'Proposal record id; if omitted, choose from active drafts.' },
    ],
    toolsUsed: [
      'get_proposal',
      'summarize_proposal',
      'list_draft_proposals',
      'find_proposals_needing_pricing',
    ],
    instructions: [
      '1. If a proposal id is provided, load it with get_proposal / summarize_proposal.',
      '2. If no proposal id is provided, use list_draft_proposals; if more than one is plausible, ask Dakota which one.',
      '3. Identify: missing price, missing scope, missing deliverables, missing client next step, readiness warnings, and suggested improvements.',
      '4. Do NOT update Airtable. Do NOT send email.',
      '5. Optionally draft recommended wording, but clearly label it as DRAFT COPY ONLY.',
    ].join('\n'),
    outputFormat:
      'A readiness checklist (what is present vs missing) + warnings + suggested improvements; any wording clearly marked "DRAFT".',
    enabled: true,
  },
  {
    workflowKey: 'client.prep_call',
    displayName: 'Prep Client Call',
    description: 'Prepare Dakota before a client call or meeting.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 0,
    approvalMode: 'none',
    triggerAliases: ['/prep-call'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'client', label: 'Client / Organization', description: 'Name or record id; falls back to the on-screen org.' },
    ],
    toolsUsed: [
      'find_client',
      'get_organization',
      'search_tasks',
      'search_communications',
      'search_leads',
      'list_draft_proposals',
    ],
    instructions: [
      '1. Resolve the client (page context or find_client).',
      '2. Pull organization detail with get_organization.',
      '3. Pull recent tasks with search_tasks.',
      '4. Pull recent communications with search_communications if available.',
      '5. Pull related proposals/leads if relevant (list_draft_proposals / search_leads).',
      '6. Summarize: who the client is, what is active, what changed recently, what Dakota probably needs to ask, any risks/blockers, and recommended talking points.',
    ].join('\n'),
    outputFormat:
      'A briefing (who / active / recent changes / questions to ask / risks / talking points) ending with a short call agenda.',
    enabled: true,
  },
  {
    workflowKey: 'lead.add_lead',
    displayName: 'Add Lead',
    description: 'Draft a clean, deduplicated lead record from rough notes. Draft-only — creates nothing.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/add-lead'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'details', label: 'Lead details', description: 'Rough notes: name, business, contact info, source, what they want.' },
    ],
    toolsUsed: ['find_client', 'search_leads', 'get_organization'],
    instructions: [
      'Goal: turn rough notes into a clean, ready-to-enter lead record that Dakota can create manually. DRAFT ONLY.',
      '1. Parse the provided details into fields: contact name, business/organization, phone, email, source, location, service interest, and notes.',
      '2. Check for duplicates: use search_leads on the name/business, and find_client to see if they are already an existing organization/client. Flag any likely match with its record id.',
      '3. If they match an existing organization, note that with get_organization context (do not assume a new lead).',
      '4. Identify missing-but-important fields and list them as "needs follow-up".',
      '5. Suggest a sensible lead status/stage and a recommended next action.',
      'Do NOT create the lead, do NOT write to Airtable, do NOT send any email/text, and do NOT call anyone. Produce a record draft + a checklist for Dakota to enter.',
    ].join('\n'),
    outputFormat:
      'A proposed lead record (field: value list) · Possible duplicates (with rec ids) · Needs follow-up · Suggested status + next action. Clearly labeled DRAFT.',
    enabled: true,
  },
  {
    workflowKey: 'website.build_intake',
    displayName: 'Website Build Intake',
    description: 'Assemble a website build packet from existing org/website records. Draft-only.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/website-build-intake', '/build-website'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'client', label: 'Client / Organization', description: 'Name or record id; falls back to the on-screen org.' },
    ],
    toolsUsed: ['find_client', 'get_organization', 'search_websites', 'get_website'],
    instructions: [
      'Goal: produce an intake/build packet so a new (or rebuilt) website can be scoped. DRAFT ONLY.',
      '1. Resolve the organization (page context or find_client) and load it with get_organization.',
      '2. Look for an existing website with search_websites / get_website; if one exists, summarize its current state and what would carry over.',
      '3. Assemble the build packet: business name, brand/voice, primary services, target audience, contact info, hours, location(s), existing domain/URL, logo/brand assets status, and any color/style notes present in the records.',
      '4. List required-but-missing inputs as a clear intake checklist (what to collect from the client).',
      '5. Propose a simple sitemap/page list and a first-draft content outline appropriate to their services.',
      'Do NOT modify the website, do NOT touch Canvas or any builder, do NOT write to Airtable, and do NOT contact the client. This is a planning packet only.',
    ].join('\n'),
    outputFormat:
      'Build packet: Business summary · Brand/assets on file · Proposed sitemap · Content outline · Intake checklist (missing inputs). Clearly labeled DRAFT.',
    enabled: true,
  },
  {
    workflowKey: 'website.archive_prep',
    displayName: 'Archive Website / Client Prep',
    description: 'Pre-offboarding checklist for archiving a website or client. Draft-only — archives nothing.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 2,
    approvalMode: 'none',
    triggerAliases: ['/archive-website', '/archive-client'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'client', label: 'Client / Organization / Website', description: 'Name or record id; falls back to the on-screen record.' },
    ],
    toolsUsed: ['find_client', 'get_organization', 'search_websites', 'get_website', 'list_managed_records'],
    instructions: [
      'Goal: prepare a safe offboarding/archive checklist so nothing is lost when a website or client is wound down. DRAFT ONLY.',
      '1. Resolve the client/website (page context or find_client / search_websites) and load detail with get_organization / get_website.',
      '2. Check for active billing or subscriptions with list_managed_records (invoices/subscriptions) — flag anything still active or unpaid that must be settled or cancelled FIRST.',
      '3. Check for active services/automations (AI receptionist, recurring work) that would need to be stopped.',
      '4. Build a pre-archive checklist: outstanding balances, domain/DNS ownership, hosting, content/asset backup, third-party logins, scheduled work, and client communications to send (as drafts).',
      '5. Call out risks and anything that should NOT be archived yet.',
      'Do NOT archive, delete, cancel, or modify any record. Do NOT cancel subscriptions or send any client message. Produce a checklist + risk list for Dakota to action manually.',
    ].join('\n'),
    outputFormat:
      'Pre-archive checklist · Active billing/services to settle first · Backup/ownership items · Risks / do-not-archive-yet · Suggested client message (DRAFT). Clearly labeled DRAFT.',
    enabled: true,
  },
  {
    workflowKey: 'deals.verify_prep',
    displayName: 'Daily Deals Verification Prep',
    description: 'Prep the verification pass for a daily-deals business. Draft-only.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/deals-verify-prep'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'business', label: 'Business', description: 'Name or record id of the deals business; falls back to the on-screen org.' },
    ],
    toolsUsed: ['find_client', 'get_organization', 'search_communications'],
    instructions: [
      'Goal: prepare a verification checklist before a daily-deals listing is confirmed. DRAFT ONLY.',
      '1. Resolve the business (page context or find_client) and load it with get_organization.',
      '2. Pull recent communications with search_communications to see the latest exchange, what was promised, and any open questions.',
      '3. Build a verification checklist: business name/address/phone match, hours, deal terms and fine print, redemption details, dates/validity window, and contact for confirmation.',
      '4. Flag anything inconsistent, missing, or that needs the business to confirm before going live.',
      '5. Draft the questions to ask the business to close any gaps (as a draft, not sent).',
      'Do NOT publish or confirm the deal, do NOT write to Airtable, and do NOT send any message. Produce a verification checklist + draft questions for Dakota.',
    ].join('\n'),
    outputFormat:
      'Verification checklist (present vs needs-confirmation) · Inconsistencies/gaps · Draft questions for the business. Clearly labeled DRAFT.',
    enabled: true,
  },
  {
    workflowKey: 'lead.callback_prep',
    displayName: 'Curiosity Callback Prep',
    description: 'Prep Dakota to call a curious/inbound lead back. Draft-only.',
    status: 'active',
    visibility: 'admin',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/callback-prep'],
    voiceTriggers: [],
    requiredInputs: [],
    optionalInputs: [
      { key: 'lead', label: 'Lead / Person', description: 'Name or record id; falls back to the on-screen lead.' },
    ],
    toolsUsed: ['find_client', 'search_leads', 'get_organization', 'search_websites'],
    instructions: [
      'Goal: prepare Dakota to make a warm, informed callback to an inbound/curious lead. DRAFT ONLY.',
      '1. Resolve the lead/person with search_leads (or page context); if they tie to an organization, load it with get_organization and find_client.',
      '2. If they already have a website, pull it with search_websites to ground the conversation in what they have now.',
      '3. Summarize: who they are, what they were curious about / what they reached out for, what we know, and what we still need to learn.',
      '4. Draft talking points and a short call opener, plus likely objections and good responses.',
      '5. Recommend a clear next step / offer appropriate to their interest.',
      'Do NOT call, text, or email anyone, and do NOT write to Airtable. Produce a call-prep briefing only.',
    ].join('\n'),
    outputFormat:
      'Callback briefing: Who they are · Why they reached out · What we know / need · Call opener + talking points · Likely objections · Recommended next step. Clearly labeled DRAFT.',
    enabled: true,
  },
  {
    workflowKey: 'personal.doghouse_mode',
    displayName: 'Doghouse Mode',
    description: 'A private, draft-only "make it right" message helper. Never sends anything.',
    status: 'active',
    visibility: 'private_hidden',
    riskLevel: 1,
    approvalMode: 'none',
    triggerAliases: ['/doghouse'],
    voiceTriggers: ["i'm in the doghouse", 'in the doghouse'],
    requiredInputs: [],
    optionalInputs: [
      { key: 'context', label: 'Context', description: 'Optional note about what happened / the vibe to hit.' },
    ],
    toolsUsed: [],
    instructions: [
      '1. Draft a sweet/funny make-up message that uses sunshine, nature, or country-song-style imagery.',
      '2. Optionally suggest a lovey-dovey country-song theme, but do not rely on live music search.',
      '3. Make it feel personal, not corporate.',
      '4. End by asking Dakota to approve, edit, or discard.',
      '5. Never text, email, or message anyone — draft only.',
    ].join('\n'),
    outputFormat: 'A short, warm draft message + an optional song theme; ends asking to approve / edit / discard.',
    enabled: true,
  },
];

export const ALICE_COMMAND_ALIASES: readonly AliceCommandAlias[] = [
  { aliasKey: 'client-summary', triggerText: '/client-summary', triggerType: 'slash', targetType: 'workflow', targetKey: 'client.project_summary', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'project-summary', triggerText: '/project-summary', triggerType: 'slash', targetType: 'workflow', targetKey: 'client.project_summary', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'prep-call', triggerText: '/prep-call', triggerType: 'slash', targetType: 'workflow', targetKey: 'client.prep_call', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'proposal-check', triggerText: '/proposal-check', triggerType: 'slash', targetType: 'workflow', targetKey: 'proposal.readiness_check', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'proposal-readiness', triggerText: '/proposal-readiness', triggerType: 'slash', targetType: 'workflow', targetKey: 'proposal.readiness_check', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'add-lead', triggerText: '/add-lead', triggerType: 'slash', targetType: 'workflow', targetKey: 'lead.add_lead', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'website-build-intake', triggerText: '/website-build-intake', triggerType: 'slash', targetType: 'workflow', targetKey: 'website.build_intake', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'build-website', triggerText: '/build-website', triggerType: 'slash', targetType: 'workflow', targetKey: 'website.build_intake', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'archive-website', triggerText: '/archive-website', triggerType: 'slash', targetType: 'workflow', targetKey: 'website.archive_prep', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'archive-client', triggerText: '/archive-client', triggerType: 'slash', targetType: 'workflow', targetKey: 'website.archive_prep', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'deals-verify-prep', triggerText: '/deals-verify-prep', triggerType: 'slash', targetType: 'workflow', targetKey: 'deals.verify_prep', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'callback-prep', triggerText: '/callback-prep', triggerType: 'slash', targetType: 'workflow', targetKey: 'lead.callback_prep', visibility: 'admin', requiresConfirmation: false, enabled: true },
  { aliasKey: 'doghouse', triggerText: '/doghouse', triggerType: 'slash', targetType: 'workflow', targetKey: 'personal.doghouse_mode', visibility: 'private_hidden', requiresConfirmation: false, enabled: true },
  { aliasKey: 'doghouse-phrase', triggerText: "I'm in the doghouse", triggerType: 'phrase', targetType: 'workflow', targetKey: 'personal.doghouse_mode', visibility: 'private_hidden', requiresConfirmation: false, enabled: true },
];

export function getAliceWorkflow(key: string | undefined): AliceWorkflowDefinition | undefined {
  return ALICE_WORKFLOWS.find((wf) => wf.workflowKey === key);
}

/** A workflow is runnable when it's enabled and not paused/retired. */
export function isAliceWorkflowRunnable(workflow: AliceWorkflowDefinition): boolean {
  return workflow.enabled && workflow.status !== 'paused' && workflow.status !== 'retired';
}

export interface AliceTriggerMatch {
  alias: AliceCommandAlias;
  workflow: AliceWorkflowDefinition;
  /** Text the user typed after the slash command (or the full message for phrases). */
  argText: string;
}

/**
 * Resolve a chat message to a workflow via a command alias. Slash commands match
 * the first token exactly; phrase/voice triggers match a case-insensitive
 * substring. Returns undefined when nothing matches or the target is not runnable.
 */
export function resolveAliceTrigger(message: string): AliceTriggerMatch | undefined {
  const trimmed = (message ?? '').trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();

  if (trimmed.startsWith('/')) {
    const firstToken = trimmed.split(/\s+/)[0]?.toLowerCase() ?? '';
    const argText = trimmed.slice(firstToken.length).trim();
    for (const alias of ALICE_COMMAND_ALIASES) {
      if (!alias.enabled || alias.triggerType !== 'slash') continue;
      if (alias.triggerText.toLowerCase() === firstToken) {
        const workflow = getAliceWorkflow(alias.targetKey);
        if (workflow && isAliceWorkflowRunnable(workflow)) return { alias, workflow, argText };
      }
    }
    return undefined;
  }

  for (const alias of ALICE_COMMAND_ALIASES) {
    if (!alias.enabled || alias.triggerType === 'slash') continue;
    if (lower.includes(alias.triggerText.toLowerCase())) {
      const workflow = getAliceWorkflow(alias.targetKey);
      if (workflow && isAliceWorkflowRunnable(workflow)) return { alias, workflow, argText: trimmed };
    }
  }
  return undefined;
}
